import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import LevelIndicator from "./LevelIndicator";
import TierBadge from "./TierBadge";
import { COURSE_LEVELS, LETTERS, levelMeta, retroDateParts, themeIcon } from "../lib/courseMeta";
/**
 * Date d'une rétrospective dans les listes compactes : l'année, puis « av. J.-C. » / « apr. J.-C. » en petit dessous.
 * className : taille et couleur de l'année.
 */
export function RetroDate({ course, className = "" }) {
  const p = retroDateParts(course);
  return (
    <span className={`font-mono leading-tight ${className}`}>
      {p.main || "—"}
      {p.suffix && <span className="block text-[10px] font-sans opacity-70 whitespace-nowrap">{p.suffix}</span>}
    </span>
  );
}
/** Pastille « Lu » (cours terminé). */
export function ReadBadge({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${className}`} data-testid="course-read-badge">
      <Check className="w-3 h-3" /> Lu
    </span>
  );
}
/** Invitation discrète pour les visiteurs : la progression demande un compte gratuit. */
export function SignupHint({ className = "" }) {
  return (
    <Link to="/login" className={`inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-orange-300 ${className}`} data-testid="course-signup-hint">
      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> Créez un compte gratuit pour suivre votre progression
      <ArrowRight className="w-3.5 h-3.5 shrink-0" />
    </Link>
  );
}
/** Fil d'Ariane : [[libellé, lien], …, [libellé]] */
export function Crumbs({ items }) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-sm text-zinc-500" aria-label="Fil d'Ariane">
      {items.map(([label, to], i) => (
        // Téléphone : la page en cours (déjà en titre juste en dessous) est masquée, le fil tient sur une ligne
        <span key={i} className={`inline-flex items-center gap-1.5 min-w-0 ${i > 0 && i === items.length - 1 && !to ? "max-sm:hidden" : ""}`}>
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-zinc-700 shrink-0" />}
          {to ? (
            <Link to={to} className="hover:text-white">{label}</Link>
          ) : (
            <span className="text-zinc-300 truncate max-w-[16rem]">{label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
/** Fourchette de niveaux d'une liste de cours : « Débutant → Avancé » */
export function levelRange(courses) {
  const ns = courses.map((c) => levelMeta(c.level)?.n).filter(Boolean);
  if (!ns.length) return "";
  const lo = Math.min(...ns), hi = Math.max(...ns);
  return COURSE_LEVELS[lo - 1].label + (hi > lo ? ` → ${COURSE_LEVELS[hi - 1].label}` : "");
}
/** Carte d'une thématique (rangée « Concepts »). */
export function ThemeCard({ theme, readIds, loggedIn, level = "all" }) {
  const Icon = themeIcon(theme.icon);
  const total = theme.courses.length;
  const done = theme.courses.filter((c) => readIds.has(c.id)).length;
  const finished = loggedIn && total > 0 && done === total;
  const shown = level === "all" ? total : theme.courses.filter((c) => c.level === level).length;
  return (
    <Link
      to={`/cours/thematique/${theme.slug}`}
      draggable={false}
      className="group snap-start shrink-0 w-[300px] block pt-2"
      data-testid={`theme-card-${theme.slug}`}
    >
      <div className="relative h-full">
        <div className="absolute -top-2 left-4 right-4 h-3 rounded-t-lg border border-b-0 border-white/5 bg-white/[0.02]" />
        <div className={`relative h-full rounded-xl border bg-card border-white/5 group-hover:border-orange-500/30 transition-colors p-5 flex flex-col ${finished ? "ring-1 ring-emerald-500/20" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 grid place-items-center rounded-lg bg-orange-500/10 border border-orange-500/25 text-orange-300">
              <Icon className="w-5 h-5" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Thématique</span>
          </div>
          <h3 className="mt-4 font-display font-bold text-lg leading-tight group-hover:text-orange-300 transition-colors">{theme.title}</h3>
          {theme.description && <p className="mt-1.5 text-sm text-zinc-400 line-clamp-2">{theme.description}</p>}
          <div className="mt-3 text-xs text-zinc-500">
            {level === "all" ? (
              <>{total} cours{levelRange(theme.courses) ? ` · ${levelRange(theme.courses)}` : ""}</>
            ) : (
              <>
                <span className="text-orange-300">{shown} cours {levelMeta(level)?.label.toLowerCase()}</span>{" "}
                <span className="text-zinc-600">sur {total}</span>
              </>
            )}
          </div>
          <div className="mt-auto pt-5 flex items-center">
            {!loggedIn ? (
              <span className="text-sm text-zinc-300 group-hover:text-orange-300 inline-flex items-center gap-1.5">
                Découvrir <ArrowRight className="w-3.5 h-3.5" />
              </span>
            ) : finished ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] uppercase tracking-[0.15em] px-2.5 py-1" data-testid="theme-finished">
                <Check className="w-3.5 h-3.5" /> Terminé
              </span>
            ) : (
              <div className="w-full">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-zinc-400">{done ? `${done} cours sur ${total} lus` : "Pas encore commencé"}</span>
                  <span className="text-zinc-500 group-hover:text-orange-300 inline-flex items-center gap-1">
                    {done ? "Continuer" : "Commencer"} <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-orange-400" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
/** Carte d'une rétrospective (frise chronologique). */
export function RetroCard({ course, isRead }) {
  return (
    <Link to={`/cours/${course.slug}`} draggable={false} className="group snap-start shrink-0 w-[260px] block" data-testid={`retro-card-${course.slug}`}>
      <div className="relative pt-6">
        <span className={`absolute top-0 left-5 w-3 h-3 rounded-full border-2 ${isRead ? "border-emerald-400 bg-emerald-400" : "border-orange-400 bg-[#0a0a0a]"}`} />
        <div className="rounded-xl border bg-card border-white/5 group-hover:border-orange-500/30 transition-colors p-5 h-[218px] flex flex-col">
          <div className="flex items-start justify-between gap-2">
            {(() => {
              // Année en grand, « av. J.-C. » / « apr. J.-C. » en plus petit à côté ; texte plus petit s'il est long
              const p = retroDateParts(course);
              const long = p.main.length + p.suffix.length > 13;
              return (
                <div className="font-mono font-semibold text-orange-300/90 leading-none whitespace-nowrap" data-testid="retro-card-date">
                  <span className={long ? "text-xl" : "text-3xl"}>{p.main || "—"}</span>
                  {p.suffix && <span className="ml-1.5 text-sm font-normal text-orange-300/70">{p.suffix}</span>}
                </div>
              );
            })()}
            <div className="flex flex-col items-end gap-1.5">
              {isRead && <ReadBadge />}
              {course.is_premium && <TierBadge tier="essentiel" locked={!!course.is_locked} />}
            </div>
          </div>
          <h3 className="mt-4 font-display font-bold leading-tight group-hover:text-orange-300 transition-colors line-clamp-2">{course.title}</h3>
          <p className="mt-1.5 text-sm text-zinc-400 line-clamp-2">{course.description}</p>
          <div className="mt-auto pt-3"><LevelIndicator level={course.level} /></div>
        </div>
      </div>
    </Link>
  );
}
/**
 * Rangée qui défile horizontalement : flèches au-dessus (passées en `arrowsRef`), glisser à la souris,
 * défilement tactile natif. Jamais de défilement automatique.
 */
export function ScrollRow({ id, children, className = "", controls }) {
  const rowRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth - 2;
      setEdges({ left: row.scrollLeft > 2, right: row.scrollLeft < max });
    };
    update();
    row.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(row);
    // Glisser à la souris (le tactile défile nativement)
    let down = false, moved = false, startX = 0, startLeft = 0;
    const onDown = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      down = true; moved = false; startX = e.clientX; startLeft = row.scrollLeft;
    };
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) { moved = true; row.style.scrollSnapType = "none"; row.style.cursor = "grabbing"; }
      if (moved) row.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      row.style.cursor = "";
      if (moved) {
        row.style.scrollSnapType = "";
        // le clic qui termine un glissement n'ouvre pas la carte
        row.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      }
    };
    row.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      row.removeEventListener("scroll", update);
      ro.disconnect();
      row.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);
  const scrollBy = (dir) => {
    const row = rowRef.current;
    if (row) row.scrollBy({ left: dir * row.clientWidth * 0.8, behavior: "smooth" });
  };
  const mask = `linear-gradient(90deg, ${edges.left ? "transparent 0, #000 48px" : "#000 0"}, ${edges.right ? "#000 calc(100% - 48px), transparent 100%" : "#000 100%"})`;
  return (
    <>
      {controls?.((dir) => scrollBy(dir), edges)}
      <div
        ref={rowRef}
        id={id}
        className={`course-row flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-px-6 px-6 pb-2 select-none ${className}`}
        style={{ WebkitMaskImage: mask, maskImage: mask }}
      >
        {children}
      </div>
    </>
  );
}
/** Flèches de défilement d'une rangée. */
export function RowArrows({ onScroll, edges }) {
  const cls = "w-9 h-9 grid place-items-center rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white disabled:opacity-30 disabled:pointer-events-none";
  return (
    <div className="hidden sm:flex items-center gap-2 shrink-0">
      <button type="button" className={cls} disabled={!edges.left} onClick={() => onScroll(-1)} aria-label="Précédent"><ChevronLeft className="w-4 h-4" /></button>
      <button type="button" className={cls} disabled={!edges.right} onClick={() => onScroll(1)} aria-label="Suivant"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}
/** Barre des lettres du glossaire (collée sous le menu). */
export function LetterBar({ active, available }) {
  return (
    <div className="sticky top-16 z-30 border-b border-white/5 bg-[#0a0a0a]/90 backdrop-blur">
      <div className="mx-auto max-w-4xl px-6 py-2.5 overflow-x-auto">
        <div className="flex gap-1 min-w-[680px]">
          {LETTERS.map((L) =>
            available.has(L) ? (
              <Link
                key={L}
                to={`/cours/glossaire/${L.toLowerCase()}`}
                className={`h-8 flex-1 grid place-items-center rounded text-xs font-semibold ${L === active ? "bg-orange-500 text-black" : "text-zinc-300 hover:bg-white/5 hover:text-orange-300"}`}
              >
                {L}
              </Link>
            ) : (
              <span key={L} className="h-8 flex-1 grid place-items-center text-xs text-zinc-700">{L}</span>
            )
          )}
        </div>
      </div>
    </div>
  );
}
